import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  CheckCircle,
  Clock,
  Plus,
  Users,
  FileText,
  AlertTriangle,
} from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Badge } from '@/app/components/ui/badge';
import { Skeleton } from '@/app/components/ui/skeleton';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Textarea } from '@/app/components/ui/textarea';
import {
  useManagementReview,
  useCompleteReview,
  useCreateActionItem,
  useUpdateActionItem,
} from '@/hooks/use-management-reviews';
import { useOrgTimeZone } from '@/hooks/use-settings';
import { formatDateOnly, formatDateTimeInTimeZone } from '@/lib/date-utils';
import type { ReviewActionItem, ActionItemStatus } from '@/lib/types';

const STATUS_BADGE: Record<string, { className: string; label: string }> = {
  scheduled: { className: 'bg-blue-100 text-blue-800', label: 'Scheduled' },
  completed: { className: 'bg-green-100 text-green-800', label: 'Completed' },
  overdue: { className: 'bg-red-100 text-red-800', label: 'Overdue' },
};

const ACTION_STATUS_BADGE: Record<string, { className: string; label: string }> = {
  open: { className: 'bg-gray-100 text-gray-800', label: 'Open' },
  in_progress: { className: 'bg-blue-100 text-blue-800', label: 'In Progress' },
  completed: { className: 'bg-green-100 text-green-800', label: 'Completed' },
  overdue: { className: 'bg-red-100 text-red-800', label: 'Overdue' },
};

export function ReviewsDetailPage() {
  const timeZone = useOrgTimeZone();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const reviewId = Number(id);
  const { data: review, isLoading } = useManagementReview(reviewId);
  const completeMutation = useCompleteReview();
  const createItemMutation = useCreateActionItem();
  const updateItemMutation = useUpdateActionItem();

  const [showCompleteForm, setShowCompleteForm] = useState(false);
  const [minutes, setMinutes] = useState('');
  const [decisions, setDecisions] = useState('');

  const [showAddItem, setShowAddItem] = useState(false);
  const [newAction, setNewAction] = useState('');
  const [newAssignedTo, setNewAssignedTo] = useState('');
  const [newDueDate, setNewDueDate] = useState('');
  const [newNotes, setNewNotes] = useState('');

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (!review) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <p className="text-gray-500">Review not found.</p>
      </div>
    );
  }

  const statusConfig = STATUS_BADGE[review.status] ?? { className: 'bg-gray-100', label: review.status };

  const handleComplete = () => {
    if (!minutes) return;
    completeMutation.mutate(
      { id: reviewId, data: { minutes, decisions } },
      {
        onSuccess: () => {
          setShowCompleteForm(false);
          setMinutes('');
          setDecisions('');
        },
      }
    );
  };

  const handleAddItem = () => {
    if (!newAction || !newAssignedTo || !newDueDate) return;
    createItemMutation.mutate(
      {
        reviewId,
        data: {
          action: newAction,
          assigned_to: newAssignedTo,
          due_date: newDueDate,
          notes: newNotes,
        },
      },
      {
        onSuccess: () => {
          setShowAddItem(false);
          setNewAction('');
          setNewAssignedTo('');
          setNewDueDate('');
          setNewNotes('');
        },
      }
    );
  };

  const handleUpdateItemStatus = (item: ReviewActionItem, newStatus: ActionItemStatus) => {
    updateItemMutation.mutate({
      reviewId,
      itemId: item.id,
      data: { status: newStatus },
    });
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      {/* Header */}
      <div className="flex items-start gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-bold text-gray-900">{review.title}</h1>
            <Badge className={statusConfig.className}>{statusConfig.label}</Badge>
          </div>
          <p className="text-sm text-gray-500 mt-1">
            {formatDateOnly(review.date)}
            {review.chairperson_username && ` — Chaired by ${review.chairperson_username}`}
          </p>
        </div>
        {review.status !== 'completed' && (
          <Button onClick={() => setShowCompleteForm(true)}>
            <CheckCircle className="h-4 w-4 mr-2" />
            Complete Review
          </Button>
        )}
      </div>

      {/* Complete Review Form */}
      {showCompleteForm && (
        <Card
          id="complete-review-fields"
          data-required-validation-boundary="true"
          className="border-green-200 bg-green-50"
        >
          <CardHeader>
            <CardTitle className="text-green-800">Complete Review</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="minutes">Meeting Minutes *</Label>
              <Textarea
                id="minutes"
                value={minutes}
                onChange={(e) => setMinutes(e.target.value)}
                placeholder="Record the meeting minutes..."
                rows={6}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="decisions">Key Decisions</Label>
              <Textarea
                id="decisions"
                value={decisions}
                onChange={(e) => setDecisions(e.target.value)}
                placeholder="Record key decisions made..."
                rows={3}
              />
            </div>
            <div className="flex gap-3">
              <Button
                onClick={handleComplete}
                data-validation-scope="complete-review-fields"
                disabled={completeMutation.isPending}
              >
                {completeMutation.isPending ? 'Completing...' : 'Mark as Completed'}
              </Button>
              <Button variant="outline" onClick={() => setShowCompleteForm(false)}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Agenda */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Agenda
            </CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="whitespace-pre-wrap text-sm text-gray-700 font-sans">
              {review.agenda}
            </pre>
          </CardContent>
        </Card>

        {/* Minutes */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Minutes
            </CardTitle>
          </CardHeader>
          <CardContent>
            {review.minutes ? (
              <pre className="whitespace-pre-wrap text-sm text-gray-700 font-sans">
                {review.minutes}
              </pre>
            ) : (
              <p className="text-sm text-gray-400 italic">
                Minutes will be recorded when the review is completed.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Decisions */}
        {review.decisions && (
          <Card>
            <CardHeader>
              <CardTitle>Key Decisions</CardTitle>
            </CardHeader>
            <CardContent>
              <pre className="whitespace-pre-wrap text-sm text-gray-700 font-sans">
                {review.decisions}
              </pre>
            </CardContent>
          </Card>
        )}

        {/* Attendees */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Attendees
            </CardTitle>
          </CardHeader>
          <CardContent>
            {review.attendees && review.attendees.length > 0 ? (
              <ul className="space-y-2">
                {review.attendees.map((att, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm">
                    <span className="font-medium text-gray-900">{att.name}</span>
                    <span className="text-gray-500">— {att.role}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-gray-400 italic">No attendees listed.</p>
            )}
          </CardContent>
        </Card>

        {/* Review Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Schedule Info
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Review Date</span>
              <span className="font-medium">{formatDateOnly(review.date)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Review Interval</span>
              <span className="font-medium">{review.review_interval_days} days</span>
            </div>
            {review.next_review_date && (
              <div className="flex justify-between">
                <span className="text-gray-500">Next Review</span>
                <span className="font-medium">{formatDateOnly(review.next_review_date)}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-gray-500">Created</span>
              <span className="font-medium">{formatDateTimeInTimeZone(review.created_at, timeZone, { dateStyle: 'medium' })}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Action Items */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              Action Items
              {review.action_items_summary.total > 0 && (
                <Badge variant="outline">{review.action_items_summary.total}</Badge>
              )}
            </CardTitle>
            <Button variant="outline" size="sm" onClick={() => setShowAddItem(true)}>
              <Plus className="h-3 w-3 mr-1" />
              Add Item
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {showAddItem && (
            <div className="mb-4 p-4 rounded-md border border-blue-200 bg-blue-50 space-y-3">
              <div className="space-y-2">
                <Label>Action *</Label>
                <Textarea
                  value={newAction}
                  onChange={(e) => setNewAction(e.target.value)}
                  placeholder="Describe the action item..."
                  rows={2}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Assigned To *</Label>
                  <Input
                    value={newAssignedTo}
                    onChange={(e) => setNewAssignedTo(e.target.value)}
                    placeholder="Name or role"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Due Date *</Label>
                  <Input
                    type="date"
                    value={newDueDate}
                    onChange={(e) => setNewDueDate(e.target.value)}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={newNotes}
                  onChange={(e) => setNewNotes(e.target.value)}
                  placeholder="Optional notes"
                  rows={2}
                />
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={handleAddItem}
                  data-validate-required="true"
                  disabled={createItemMutation.isPending}
                >
                  {createItemMutation.isPending ? 'Adding...' : 'Add'}
                </Button>
                <Button size="sm" variant="outline" onClick={() => setShowAddItem(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {review.action_items.length === 0 ? (
            <p className="text-sm text-gray-400 italic text-center py-4">
              No action items yet. Add items to track follow-up tasks.
            </p>
          ) : (
            <div className="space-y-3">
              {review.action_items.map((item) => {
                const itemConfig = ACTION_STATUS_BADGE[item.status] ?? {
                  className: 'bg-gray-100',
                  label: item.status,
                };
                return (
                  <div
                    key={item.id}
                    className="flex items-start gap-3 rounded-md border p-3"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">{item.action}</p>
                      <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                        <span>Assigned: {item.assigned_to}</span>
                        <span>Due: {formatDateOnly(item.due_date)}</span>
                        {item.completed_at && (
                          <span>Completed: {formatDateTimeInTimeZone(item.completed_at, timeZone, { dateStyle: 'medium' })}</span>
                        )}
                      </div>
                      {item.notes && (
                        <p className="mt-1 text-xs text-gray-500">{item.notes}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge className={itemConfig.className}>{itemConfig.label}</Badge>
                      {item.status !== 'completed' && (
                        <select
                          value={item.status}
                          onChange={(e) =>
                            handleUpdateItemStatus(item, e.target.value as ActionItemStatus)
                          }
                          className="rounded border border-gray-300 px-2 py-1 text-xs"
                          disabled={updateItemMutation.isPending}
                        >
                          <option value="open">Open</option>
                          <option value="in_progress">In Progress</option>
                          <option value="completed">Completed</option>
                        </select>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
